import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  LanguageService,
  Order,
  QueryParams,
  TotvsListParamsFactoryService,
  TotvsPage,
} from 'ats-ui-lib';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { JobApplication, JobApplicationsForApplicant } from '../../models/job-application.model';
import {
  JobOpportunityApplication,
  JobOpportunityStepsForJobApplication,
} from '../../models/job-opportunity-application.model';
import { RequestServiceService } from '../request/request-service.service';

@Injectable({
  providedIn: 'root',
})
export class JobApplicationService {
  constructor(
    // for logged in requests
    private httpClient: HttpClient,
    private paramsFactory: TotvsListParamsFactoryService,
    // for not logged in requests
    private requestService: RequestServiceService,
    private languageService: LanguageService,
  ) {}

  fromApplicant(jobApplicationId: string): Observable<JobOpportunityApplication> {
    return this.httpClient.get<JobOpportunityApplication>(
      `${environment.applicantApiEndpoint}/jobapplications/from-applicant/${jobApplicationId}`,
    );
  }

  getMostRecentApplicationsFromCurrentApplicant(): Observable<TotvsPage<JobApplication>> {
    return this.httpClient.get<TotvsPage<JobApplication>>(
      `${environment.applicantApiEndpoint}/jobapplications/from-applicant`,
      {
        params: {
          pageSize: 12,
          order: '-applicationDate',
        },
      },
    );
  }

  getApplicationCountFromCurrentApplicant(): Observable<number> {
    return this.httpClient
      .get<{
        count: number;
      }>(`${environment.applicantApiEndpoint}/jobapplications/count-from-applicant`)
      .pipe(map((result) => result.count));
  }

  getAll(
    page: number,
    pageSize: number,
    filter?: string,
    order?: Order<JobApplicationsForApplicant>,
    advancedFilter?: QueryParams,
  ): Observable<TotvsPage<JobApplicationsForApplicant>> {
    const params = this.paramsFactory.createParams<JobApplicationsForApplicant>(
      page,
      pageSize,
      filter,
      order,
      advancedFilter,
    );
    return this.httpClient.get<TotvsPage<JobApplicationsForApplicant>>(
      `${environment.applicantApiEndpoint}/jobapplications/from-applicant`,
      {
        params,
      },
    );
  }

  public getJobOpportunityStepsForJobApplication(
    jobApplicationId: string,
    jobOpportunityId: string,
  ): Observable<JobOpportunityStepsForJobApplication> {
    return this.httpClient.get<JobOpportunityStepsForJobApplication>(
      `${environment.applicantApiEndpoint}/job-opportunity/steps-relevant-info/${jobOpportunityId}/${jobApplicationId}`,
    );
  }

  public async decideJobApplicationRecommendationConfirmation(
    id: string,
    secret: string,
    shouldAccept: boolean,
  ): Promise<void> {
    const action = shouldAccept ? 'accept' : 'reject';
    await this.requestService.requestFetch(
      `${environment.applicantApiEndpoint}/jobapplications/recommendation-confirmation/${id}/${action}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.languageService.getAcceptLanguageHeader(),
        },
        mode: 'cors',
        body: JSON.stringify({ secret }),
      },
    );
  }

  public async acceptJobApplicationProposalLetter(id: string, secret: string): Promise<void> {
    await this.requestService.requestFetch(
      `${environment.applicantApiEndpoint}/jobapplications/proposal-letter-confirmation/${id}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.languageService.getAcceptLanguageHeader(),
        },
        mode: 'cors',
        body: JSON.stringify({ secret }),
      },
    );
  }
}
